import React from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { RefineKbarProvider } from '@refinedev/kbar';
import { ErrorComponent, ThemedLayoutV2, ThemedSiderV2 } from '@refinedev/antd';
import { ConfigProvider, App as AntdApp, Layout, Avatar, Switch, Typography, Space } from 'antd';
import { Refine, Authenticated, useGetIdentity } from '@refinedev/core';
import routerProvider, { CatchAllNavigate } from '@refinedev/react-router-v6';
import { UserDto } from '@wellverse/shared-ts';
import { RefineKbar } from '@refinedev/kbar';
import {
  MoonOutlined,
  SunOutlined,
  CoffeeOutlined,
  FileTextOutlined,
  PictureOutlined,
  TeamOutlined,
  DashboardOutlined,
  ShopOutlined,
  TrophyOutlined,
  CrownOutlined,
  ProfileOutlined,
  SettingOutlined,
  RobotOutlined,
  TagOutlined,
  BugOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { dataProvider } from './providers/dataProvider';
import { authProvider } from './providers/authProvider';
import { ThemeProvider, useTheme, getThemeConfig } from './providers/themeProvider';
import { Login } from './pages/login';
import { UsdaSearch } from './pages/usda-search';
import { SystemCache } from './pages/system-cache';
import {
  UserList,
  UserCreate,
  UserEdit,
  UserShow,
} from './resources/users';
import {
  RoleList,
  RoleCreate,
  RoleEdit,
  RoleShow,
} from './resources/roles';
import { PermissionList } from './resources/permissions';
import {
  ContentList,
  ContentCreate,
  ContentEdit,
  ContentShow,
} from './resources/content';
import {
  IngredientCatalogList,
  IngredientCatalogCreate,
  IngredientCatalogEdit,
  IngredientCatalogShow,
} from './resources/ingredient-catalog';
import {
  DishCatalogList,
  DishCatalogCreate,
  DishCatalogEdit,
  DishCatalogShow,
} from './resources/dish-catalog';
import {
  TaggingScan,
  TaggingLogList,
  TaggingLogShow,
} from './resources/tagging';
import { AiReviewList } from './resources/ai-review';
import { QualityCleanupList } from './resources/quality-cleanup';
import {
  KnowledgeBaseList,
  KnowledgeBaseCreate,
  KnowledgeBaseShow,
} from './resources/knowledge-bases';
import {
  SurveyList,
  SurveyCreate,
  SurveyEdit,
  SurveyShow,
} from './resources/surveys';
import {
  SurveyScreenList,
  SurveyScreenCreate,
  SurveyScreenEdit,
  SurveyScreenShow,
} from './resources/survey-screens';
import {
  SurveyQuestionList,
  SurveyQuestionCreate,
  SurveyQuestionEdit,
  SurveyQuestionShow,
} from './resources/survey-questions';
import { FoodLogList, FoodLogShow } from './resources/food-logs';
import { VisualEntityList, VisualEntityShow } from './resources/visual-entities';
import { VisionQueueJobList, VisionQueueJobShow } from './resources/vision-queue-jobs';
import { MediaAssetList } from './resources/media-assets';
import { SurveyResponseList, SurveyResponseShow } from './resources/survey-responses';
import {
  EquipmentCatalogList,
  EquipmentCatalogCreate,
  EquipmentCatalogEdit,
  EquipmentCatalogShow,
} from './resources/equipment-catalog';
import { GymBranchList, GymBranchEdit } from './resources/gym-branches';
import {
  RestaurantList,
  RestaurantCreate,
  RestaurantEdit,
  RestaurantShow,
} from './resources/restaurants';
import {
  MealList,
  MealCreate,
  MealEdit,
  MealShow,
} from './resources/meals';
import { GamificationTaskList, GamificationTaskCreate, GamificationTaskEdit } from './resources/gamification-tasks';
import { PetSkinList, PetSkinCreate, PetSkinEdit } from './resources/pet-skins';
import {
  PetSkinCategoryList,
  PetSkinCategoryCreate,
  PetSkinCategoryEdit,
} from './resources/pet-skin-categories';
import { CoinTransactionList } from './resources/coin-transactions';
import { GamificationAuditList } from './resources/gamification-audit';
import { XpTransactionList } from './resources/xp-transactions';
import { GamificationSettingsPage } from './resources/gamification-settings';
import { ItemGrantPage } from './resources/item-grants';
import {
  BadgeList,
  BadgeCreate,
  BadgeEdit,
  BadgeShow,
} from './resources/badges';
import {
  LevelList,
  LevelCreate,
  LevelEdit,
  LevelShow,
} from './resources/levels';
import {
  ExerciseList,
  ExerciseCreate,
  ExerciseEdit,
  ExerciseShow,
} from './resources/exercises';
import {
  WorkoutPlanList,
  WorkoutPlanCreate,
  WorkoutPlanEdit,
  WorkoutPlanShow,
} from './resources/workout-plans';
import { WorkoutLogList, WorkoutLogShow } from './resources/workout-logs';
import { BodyMetricLogList, BodyMetricLogShow } from './resources/body-metric-logs';
import { SleepStepsList, SleepStepsShow } from './resources/sleep-steps';
import { BodyMetricSourceList, BodyMetricSourceShow } from './resources/body-metric-sources';
import {
  HealthSyncRecordList,
  HealthSyncRecordShow,
  HealthSyncRecordEdit,
  HealthSyncRecordCreate,
} from './resources/health-sync-records';
import { FastingLogList, FastingLogShow } from './resources/fasting-logs';
import { MoodLogList, MoodLogShow } from './resources/mood-logs';
import { FeedbackReportList, FeedbackReportShow } from './resources/feedback-reports';
import { AccountDeletionRequestList, AccountDeletionRequestShow } from './resources/account-deletion-requests';
import { SettingsPage } from './resources/settings';
import { OtpDeliveryLogList } from './resources/otp-delivery-logs';
import {
  SubscriptionPlanList,
  SubscriptionPlanCreate,
  SubscriptionPlanEdit,
  SubscriptionPlanShow,
} from './resources/subscription-plans';
import { SubscriptionList, SubscriptionShow } from './resources/subscriptions';
import { ReferralList, ReferralShow } from './resources/referrals';
import { ReferralCodeList, ReferralCodeCreate, ReferralCodeEdit } from './resources/referral-codes';
import { MemberPermissionsPage } from './resources/member-permissions';
import {
  LanguageList,
  LanguageCreate,
  LanguageEdit,
  LanguageShow,
} from './resources/languages';
import {
  NotificationReminderList,
  NotificationReminderCreate,
  NotificationReminderEdit,
  NotificationReminderShow,
} from './resources/notification-reminders';
import {
  SystemMessageList,
  SystemMessageCreate,
  SystemMessageEdit,
  SystemMessageShow,
} from './resources/system-messages';
import {
  AppStringList,
  AppStringCreate,
  AppStringEdit,
  AppStringShow,
} from './resources/app-strings';
import {
  LlmProviderList,
  LlmProviderCreate,
  LlmProviderEdit,
  LlmProviderShow,
  LlmPromptList,
  LlmPromptCreate,
  LlmPromptEdit,
  LlmPromptShow,
  LlmCallLogList,
  LlmCallLogShow,
  LlmPlayground,
  LlmModelList,
  LlmModelCreate,
  LlmModelEdit,
  LlmModelShow,
} from './resources/llm';

const resourceToPermission: Record<string, string> = {
  users: 'USERS',
  roles: 'ROLES',
  permissions: 'PERMISSIONS',
  content: 'CONTENT',
  'ingredient-catalog': 'INGREDIENT_CATALOG',
  'dish-catalog': 'DISH_CATALOG',
  'dish-catalog-scan': 'DISH_CATALOG',
  'tagging': 'DISH_CATALOG',
  'tagging-logs': 'DISH_CATALOG',
  'ai-review': 'DISH_CATALOG',
  'quality-cleanup': 'DISH_CATALOG',
  'usda-search': 'INGREDIENT_CATALOG',
  'knowledge-bases': 'KNOWLEDGE_BASE',
  'visual-entities': 'VISION',
  'vision-queue-jobs': 'VISION',
  surveys: 'SURVEYS',
  'survey-screens': 'SURVEY_SCREENS',
  'survey-questions': 'SURVEY_QUESTIONS',
  'food-logs': 'FOOD_LOGS',
  'media-assets': 'MEDIA',
  'survey-responses': 'SURVEY_RESPONSES',
  'equipment-catalog': 'EQUIPMENT_CATALOG',
  'gym-branches': 'EQUIPMENT_CATALOG',
  restaurants: 'RESTAURANTS',
  meals: 'MEALS',
  'gamification-tasks': 'GAMIFICATION',
  'pet-skins': 'GAMIFICATION',
  'pet-skin-categories': 'GAMIFICATION',
  'gamification-settings': 'GAMIFICATION',
  'gamification-audit': 'GAMIFICATION',
  'xp-transactions': 'GAMIFICATION',
  'item-grants': 'GAMIFICATION',
  'coin-transactions': 'GAMIFICATION',
  'badges': 'BADGES',
  'levels': 'LEVELS',
  'exercises': 'EXERCISES',
  'workout-plans': 'WORKOUT_PLANS',
  'workout-logs': 'WORKOUT_LOGS',
  'body-metric-logs': 'BODY_METRICS',
  'sleep-steps': 'BODY_METRICS',
  'body-metric-sources': 'BODY_SOURCES',
  'health-sync-records': 'HEALTH_SYNC',
  'fasting-logs': 'FASTING_LOGS',
  'mood-logs': 'MOOD_LOGS',
  'feedback-reports': 'FEEDBACK_REPORTS',
  'account-deletion-requests': 'ACCOUNT_DELETION_REQUESTS',
  'subscription-plans': 'SUBSCRIPTION_PLANS',
  'subscriptions': 'SUBSCRIPTIONS',
  'referrals': 'SUBSCRIPTIONS',
  'referral-codes': 'SUBSCRIPTIONS',
  'member-permissions': 'SUBSCRIPTIONS',
  'languages': 'LANGUAGES',
  'notification-reminders': 'NOTIFICATION_REMINDERS',
  'system-messages': 'SYSTEM_MESSAGES',
  'app-strings': 'APP_STRINGS',
  'lang-pack-releases': 'APP_STRINGS',
  'llm-providers': 'LLM_PROVIDERS',
  'llm-prompts': 'LLM_PROMPTS',
  'llm-call-logs': 'LLM_CALL_LOGS',
  'llm-playground': 'LLM_PROVIDERS',
  'llm-models': 'LLM_MODELS',
  'otp-delivery-logs': 'SETTINGS',
  'system-settings': 'SETTINGS',
};

const actionMap: Record<string, string> = {
  list: 'read',
  create: 'create',
  edit: 'update',
  show: 'read',
  delete: 'delete',
  field: 'read',
};

const accessControlProvider = {
  can: async ({ resource, action }: { resource?: string; action?: string }) => {
    const result = (await authProvider.getPermissions?.()) as
      | { permissions?: string[] }
      | string[]
      | undefined
      | null;

    let perms: string[] = [];
    if (Array.isArray(result)) {
      perms = result;
    } else if (result && typeof result === 'object') {
      perms = result.permissions ?? [];
    }

    if (!resource) {
      return { can: true };
    }

    const permResource = resourceToPermission[resource] ?? resource.toUpperCase();
    const permAction = actionMap[action ?? ''] ?? action ?? 'read';
    const required = `${permResource}:${permAction}`;
    const wildcard = `${permResource}:*`;
    const manage = `${permResource}:manage`;

    const hasPermission = perms.some(
      (p) => {
        const pl = p.toLowerCase();
        return (
          pl === required.toLowerCase() ||
          pl === wildcard.toLowerCase() ||
          pl === manage.toLowerCase() ||
          pl === '*:*'
        );
      },
    );

    return {
      can: hasPermission,
      reason: hasPermission ? undefined : `Missing permission: ${required}`,
    };
  },
};

const { Header: AntHeader } = Layout;

const FixedSider: React.FC<any> = (props) => <ThemedSiderV2 {...props} fixed />;

const LogoTitle: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { isDark } = useTheme();
  return (
    <Space size={10} align="center">
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background: '#3ecf8e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 700,
          color: '#03231c',
          flexShrink: 0,
        }}
      >
        W
      </div>
      {!collapsed && (
        <Typography.Text
          strong
          style={{
            color: isDark ? '#fafafa' : '#1f1f1f',
            fontSize: 16,
            whiteSpace: 'nowrap',
          }}
        >
          Wooki AI CMS
        </Typography.Text>
      )}
    </Space>
  );
};

const CustomHeader: React.FC = () => {
  const { isDark, toggle } = useTheme();
  const { data: userRaw } = useGetIdentity();
  const user = userRaw as UserDto | null | undefined;

  return (
    <AntHeader
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: isDark ? '#1c1c1c' : '#ffffff',
        borderBottom: `1px solid ${isDark ? '#2e2e2e' : '#e8e8e8'}`,
        padding: '0 24px',
        height: 64,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
      }}
    >
      <div />

      <Space align="center" size={16} style={{ marginLeft: 'auto' }}>
        {user && (
          <Space align="center" size={8}>
            <Avatar
              size="small"
              style={{ backgroundColor: '#3ecf8e', color: '#fff' }}
            >
              {user.email?.[0]?.toUpperCase()}
            </Avatar>
            <Typography.Text
              style={{
                color: isDark ? '#b4b4b4' : '#595959',
                fontSize: 14,
              }}
            >
              {user.email}
            </Typography.Text>
          </Space>
        )}
        <Switch
          checked={isDark}
          onChange={toggle}
          checkedChildren={<MoonOutlined />}
          unCheckedChildren={<SunOutlined />}
        />
      </Space>
    </AntHeader>
  );
};

const AppContent: React.FC = () => {
  const { isDark } = useTheme();
  const antdTheme = getThemeConfig(isDark);

  return (
    <ConfigProvider theme={antdTheme}>
      <AntdApp>
        <RefineKbarProvider>
          <Refine
            routerProvider={routerProvider}
            dataProvider={dataProvider}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            resources={[
              {
                name: 'food-nutrition',
                meta: { label: 'Food & Nutrition', icon: <CoffeeOutlined /> },
              },
              {
                name: 'ingredient-catalog',
                list: '/ingredient-catalog',
                create: '/ingredient-catalog/create',
                edit: '/ingredient-catalog/edit/:id',
                show: '/ingredient-catalog/show/:id',
                meta: { parent: 'food-nutrition', label: 'Ingredient Catalog' },
              },
              {
                name: 'dish-catalog',
                list: '/dish-catalog',
                create: '/dish-catalog/create',
                edit: '/dish-catalog/edit/:id',
                show: '/dish-catalog/show/:id',
                meta: { parent: 'food-nutrition', label: 'Dish Catalog' },
              },
              {
                name: 'dish-catalog-scan',
                list: '/tagging/scan',
                meta: { parent: 'tagging', label: 'Scan to Tag' },
              },
              {
                name: 'tagging-logs',
                list: '/tagging/logs',
                show: '/tagging/logs/show/:id',
                meta: { parent: 'tagging', label: 'Manual Scan Logs' },
              },
              {
                name: 'ai-review',
                list: '/tagging/ai-review',
                meta: { parent: 'tagging', label: 'AI Scan Logs' },
              },
              {
                name: 'quality-cleanup',
                list: '/tagging/quality-cleanup',
                meta: { parent: 'tagging', label: 'Quality Cleanup' },
              },
              {
                name: 'usda-search',
                list: '/ingredient-catalog/usda-search',
                meta: { parent: 'food-nutrition', label: 'USDA Import' },
              },
              {
                name: 'system-cache',
                list: '/system-cache',
                meta: { label: 'Cache', icon: <SettingOutlined /> },
              },
              {
                name: 'tagging',
                meta: { label: 'Tagging', icon: <TagOutlined /> },
              },
              {
                name: 'restaurants-meals',
                meta: { label: 'Restaurants & Meals', icon: <ShopOutlined /> },
              },
              {
                name: 'restaurants',
                list: '/restaurants',
                create: '/restaurants/create',
                edit: '/restaurants/edit/:id',
                show: '/restaurants/show/:id',
                meta: { parent: 'restaurants-meals', label: 'Restaurants' },
              },
              {
                name: 'meals',
                list: '/meals',
                create: '/meals/create',
                edit: '/meals/edit/:id',
                show: '/meals/show/:id',
                meta: { parent: 'restaurants-meals', label: 'Meals' },
              },
              {
                name: 'content-surveys',
                meta: { label: 'Content & Surveys', icon: <FileTextOutlined /> },
              },
              {
                name: 'content',
                list: '/content',
                create: '/content/create',
                edit: '/content/edit/:id',
                show: '/content/show/:id',
                meta: { parent: 'content-surveys', label: 'Content' },
              },
              {
                name: 'surveys',
                list: '/surveys',
                create: '/surveys/create',
                edit: '/surveys/edit/:id',
                show: '/surveys/show/:id',
                meta: { parent: 'content-surveys', label: 'Surveys' },
              },
              {
                name: 'survey-screens',
                create: '/survey-screens/create',
                edit: '/survey-screens/edit/:id',
                show: '/survey-screens/show/:id',
                meta: { parent: 'content-surveys', label: 'Survey Screens' },
              },
              {
                name: 'survey-questions',
                create: '/survey-questions/create',
                edit: '/survey-questions/edit/:id',
                show: '/survey-questions/show/:id',
                meta: { parent: 'content-surveys', label: 'Survey Questions' },
              },
              {
                name: 'survey-responses',
                list: '/survey-responses',
                show: '/survey-responses/show/:id',
                meta: { parent: 'content-surveys', label: 'Survey Responses' },
              },
              {
                name: 'media-knowledge',
                meta: { label: 'Media & Knowledge', icon: <PictureOutlined /> },
              },
              {
                name: 'media-assets',
                list: '/media-assets',
                meta: { parent: 'media-knowledge', label: 'Media Library' },
              },
              {
                name: 'knowledge-bases',
                list: '/knowledge-bases',
                create: '/knowledge-bases/create',
                show: '/knowledge-bases/show/:id',
                meta: { parent: 'media-knowledge', label: 'Knowledge Bases' },
              },
              {
                name: 'visual-entities',
                list: '/visual-entities',
                show: '/visual-entities/show/:id',
                meta: { parent: 'media-knowledge', label: 'Visual Entities' },
              },
              {
                name: 'vision-queue-jobs',
                list: '/vision-queue-jobs',
                show: '/vision-queue-jobs/show/:id',
                meta: { parent: 'media-knowledge', label: 'Vision Queue Jobs' },
              },
              {
                name: 'fitness',
                meta: { label: 'Fitness', icon: <DashboardOutlined /> },
              },
              {
                name: 'equipment-catalog',
                list: '/equipment-catalog',
                create: '/equipment-catalog/create',
                edit: '/equipment-catalog/edit/:id',
                show: '/equipment-catalog/show/:id',
                meta: { parent: 'fitness', label: 'Equipment Catalog' },
              },
              {
                name: 'gym-branches',
                list: '/gym-branches',
                edit: '/gym-branches/edit/:id',
                meta: { parent: 'fitness', label: 'Gym Branch Equipment' },
              },
              {
                name: 'exercises',
                list: '/exercises',
                create: '/exercises/create',
                edit: '/exercises/edit/:id',
                show: '/exercises/show/:id',
                meta: { parent: 'fitness', label: 'Exercises' },
              },
              {
                name: 'workout-plans',
                list: '/workout-plans',
                create: '/workout-plans/create',
                edit: '/workout-plans/edit/:id',
                show: '/workout-plans/show/:id',
                meta: { parent: 'fitness', label: 'Workout Plans' },
              },
              {
                name: 'user-activity',
                meta: { label: 'User Activity', icon: <ProfileOutlined /> },
              },
              {
                name: 'food-logs',
                list: '/food-logs',
                show: '/food-logs/show/:id',
                meta: { parent: 'user-activity', label: 'Food Logs' },
              },
              {
                name: 'workout-logs',
                list: '/workout-logs',
                show: '/workout-logs/show/:id',
                meta: { parent: 'user-activity', label: 'Workout Logs' },
              },
              {
                name: 'body-metric-logs',
                list: '/body-metric-logs',
                show: '/body-metric-logs/show/:id',
                meta: { parent: 'user-activity', label: 'Body Metrics' },
              },
              {
                name: 'sleep-steps',
                list: '/sleep-steps',
                show: '/sleep-steps/show/:id',
                meta: { parent: 'user-activity', label: 'Sleep & Steps' },
              },
              {
                name: 'body-metric-sources',
                list: '/body-metric-sources',
                show: '/body-metric-sources/show/:id',
                meta: { parent: 'user-activity', label: 'Device Sources' },
              },
              {
                name: 'health-sync-records',
                list: '/health-sync-records',
                create: '/health-sync-records/create',
                edit: '/health-sync-records/edit/:id',
                show: '/health-sync-records/show/:id',
                meta: { parent: 'user-activity', label: 'Health Sync' },
              },
              {
                name: 'fasting-logs',
                list: '/fasting-logs',
                show: '/fasting-logs/show/:id',
                meta: { parent: 'user-activity', label: 'Fasting Logs' },
              },
              {
                name: 'mood-logs',
                list: '/mood-logs',
                show: '/mood-logs/show/:id',
                meta: { parent: 'user-activity', label: 'Mood Logs' },
              },
              {
                name: 'feedback-reports',
                list: '/feedback-reports',
                show: '/feedback-reports/show/:id',
                meta: { label: 'Reports', icon: <BugOutlined /> },
              },
              {
                name: 'account-deletion-requests',
                list: '/account-deletion-requests',
                show: '/account-deletion-requests/show/:id',
                meta: { label: 'Deletion Requests', icon: <UserDeleteOutlined /> },
              },
              {
                name: 'subscriptions-group',
                meta: { label: 'Subscriptions', icon: <CrownOutlined /> },
              },
              {
                name: 'subscription-plans',
                list: '/subscription-plans',
                create: '/subscription-plans/create',
                edit: '/subscription-plans/edit/:id',
                show: '/subscription-plans/show/:id',
                meta: { parent: 'subscriptions-group', label: 'Plans' },
              },
              {
                name: 'subscriptions',
                list: '/subscriptions',
                show: '/subscriptions/show/:id',
                meta: { parent: 'subscriptions-group', label: 'User Subscriptions' },
              },
              {
                name: 'referrals',
                list: '/referrals',
                show: '/referrals/show/:id',
                meta: { parent: 'subscriptions-group', label: 'Referrals' },
              },
              {
                name: 'referral-codes',
                list: '/referral-codes',
                create: '/referral-codes/create',
                edit: '/referral-codes/edit/:id',
                meta: { parent: 'subscriptions-group', label: 'Referral Codes' },
              },
              {
                name: 'member-permissions',
                list: '/member-permissions',
                meta: { parent: 'subscriptions-group', label: 'Member Permissions' },
              },
              {
                name: 'gamification',
                meta: { label: 'Gamification', icon: <TrophyOutlined /> },
              },
              {
                name: 'gamification-tasks',
                list: '/gamification-tasks',
                create: '/gamification-tasks/create',
                edit: '/gamification-tasks/edit/:id',
                meta: { parent: 'gamification', label: 'Daily Task Pool' },
              },
              {
                name: 'pet-skins',
                list: '/pet-skins',
                create: '/pet-skins/create',
                edit: '/pet-skins/edit/:id',
                meta: { parent: 'gamification', label: 'Pet Skins' },
              },
              {
                name: 'pet-skin-categories',
                list: '/pet-skin-categories',
                create: '/pet-skin-categories/create',
                edit: '/pet-skin-categories/edit/:id',
                meta: { parent: 'gamification', label: 'Pet Skin Categories' },
              },
              {
                name: 'gamification-settings',
                list: '/gamification-settings',
                meta: { parent: 'gamification', label: 'Settings' },
              },
              {
                name: 'gamification-audit',
                list: '/gamification-audit',
                meta: { parent: 'gamification', label: 'Audit Log' },
              },
              {
                name: 'xp-transactions',
                list: '/xp-transactions',
                meta: { parent: 'gamification', label: 'XP Ledger' },
              },
              {
                name: 'item-grants',
                list: '/item-grants',
                meta: { parent: 'gamification', label: 'Item Grants' },
              },
              {
                name: 'badges',
                list: '/badges',
                create: '/badges/create',
                edit: '/badges/edit/:id',
                show: '/badges/show/:id',
                meta: { parent: 'gamification', label: 'Badges' },
              },
              {
                name: 'levels',
                list: '/levels',
                create: '/levels/create',
                edit: '/levels/edit/:id',
                show: '/levels/show/:id',
                meta: { parent: 'gamification', label: 'Levels' },
              },
              {
                name: 'coin-transactions',
                list: '/coin-transactions',
                meta: { parent: 'gamification', label: 'Coin Ledger' },
              },
              {
                name: 'user-management',
                meta: { label: 'User Management', icon: <TeamOutlined /> },
              },
              {
                name: 'users',
                list: '/users',
                create: '/users/create',
                edit: '/users/edit/:id',
                show: '/users/show/:id',
                meta: { parent: 'user-management', label: 'Users' },
              },
              {
                name: 'roles',
                list: '/roles',
                create: '/roles/create',
                edit: '/roles/edit/:id',
                show: '/roles/show/:id',
                meta: { parent: 'user-management', label: 'Roles' },
              },
              {
                name: 'permissions',
                list: '/permissions',
                meta: { parent: 'user-management', label: 'Permissions' },
              },
              {
                name: 'settings',
                meta: { label: 'Settings', icon: <SettingOutlined /> },
              },
              {
                name: 'ai-llm',
                meta: { label: 'AI & LLM', icon: <RobotOutlined /> },
              },
              {
                name: 'llm-providers',
                list: '/llm-providers',
                create: '/llm-providers/create',
                edit: '/llm-providers/edit/:id',
                show: '/llm-providers/show/:id',
                meta: { parent: 'ai-llm', label: 'LLM Providers' },
              },
              {
                name: 'llm-prompts',
                list: '/llm-prompts',
                create: '/llm-prompts/create',
                edit: '/llm-prompts/edit/:id',
                show: '/llm-prompts/show/:id',
                meta: { parent: 'ai-llm', label: 'LLM Prompts' },
              },
              {
                name: 'llm-call-logs',
                list: '/llm-call-logs',
                show: '/llm-call-logs/show/:id',
                meta: { parent: 'ai-llm', label: 'Call Logs' },
              },
              {
                name: 'llm-models',
                list: '/llm-models',
                create: '/llm-models/create',
                edit: '/llm-models/edit/:id',
                show: '/llm-models/show/:id',
                meta: { parent: 'ai-llm', label: 'Models' },
              },
              {
                name: 'llm-playground',
                list: '/llm-playground',
                meta: { parent: 'ai-llm', label: 'Playground' },
              },
              {
                name: 'system-settings',
                list: '/system-settings',
                meta: { parent: 'settings', label: 'System Settings' },
              },
              {
                name: 'otp-delivery-logs',
                list: '/otp-delivery-logs',
                meta: { parent: 'settings', label: 'OTP Delivery Logs' },
              },
              {
                name: 'languages',
                list: '/languages',
                create: '/languages/create',
                edit: '/languages/edit/:id',
                show: '/languages/show/:id',
                meta: { parent: 'settings', label: 'Languages' },
              },
              {
                name: 'notification-reminders',
                list: '/notification-reminders',
                create: '/notification-reminders/create',
                edit: '/notification-reminders/edit/:id',
                show: '/notification-reminders/show/:id',
                meta: { parent: 'settings', label: 'Notification Reminders' },
              },
              {
                name: 'system-messages',
                list: '/system-messages',
                create: '/system-messages/create',
                edit: '/system-messages/edit/:id',
                show: '/system-messages/show/:id',
                meta: { parent: 'settings', label: 'System Messages' },
              },
              {
                name: 'app-strings',
                list: '/app-strings',
                create: '/app-strings/create',
                edit: '/app-strings/edit/:id',
                show: '/app-strings/show/:id',
                meta: { parent: 'settings', label: 'App Strings' },
              },
            ]}
            options={{
              disableTelemetry: true,
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              useNewQueryKeys: true,
            }}
          >
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                element={
                  <Authenticated
                    key="authenticated"
                    fallback={<CatchAllNavigate to="/login" />}
                  >
                    <ThemedLayoutV2 Header={CustomHeader} Sider={FixedSider} Title={LogoTitle}>
                      <Outlet />
                    </ThemedLayoutV2>
                  </Authenticated>
                }
              >
                <Route index element={<div style={{ color: isDark ? '#b4b4b4' : '#595959' }}>Select a resource from the sidebar</div>} />
                <Route path="users">
                  <Route index element={<UserList />} />
                  <Route path="create" element={<UserCreate />} />
                  <Route path="edit/:id" element={<UserEdit />} />
                  <Route path="show/:id" element={<UserShow />} />
                </Route>
                <Route path="roles">
                  <Route index element={<RoleList />} />
                  <Route path="create" element={<RoleCreate />} />
                  <Route path="edit/:id" element={<RoleEdit />} />
                  <Route path="show/:id" element={<RoleShow />} />
                </Route>
                <Route path="permissions">
                  <Route index element={<PermissionList />} />
                </Route>
                <Route path="system-settings">
                  <Route index element={<SettingsPage />} />
                </Route>
                <Route path="otp-delivery-logs">
                  <Route index element={<OtpDeliveryLogList />} />
                </Route>
                <Route path="languages">
                  <Route index element={<LanguageList />} />
                  <Route path="create" element={<LanguageCreate />} />
                  <Route path="edit/:id" element={<LanguageEdit />} />
                  <Route path="show/:id" element={<LanguageShow />} />
                </Route>
                <Route path="notification-reminders">
                  <Route index element={<NotificationReminderList />} />
                  <Route path="create" element={<NotificationReminderCreate />} />
                  <Route path="edit/:id" element={<NotificationReminderEdit />} />
                  <Route path="show/:id" element={<NotificationReminderShow />} />
                </Route>
                <Route path="system-messages">
                  <Route index element={<SystemMessageList />} />
                  <Route path="create" element={<SystemMessageCreate />} />
                  <Route path="edit/:id" element={<SystemMessageEdit />} />
                  <Route path="show/:id" element={<SystemMessageShow />} />
                </Route>
                <Route path="app-strings">
                  <Route index element={<AppStringList />} />
                  <Route path="create" element={<AppStringCreate />} />
                  <Route path="edit/:id" element={<AppStringEdit />} />
                  <Route path="show/:id" element={<AppStringShow />} />
                </Route>
                <Route path="content">
                  <Route index element={<ContentList />} />
                  <Route path="create" element={<ContentCreate />} />
                  <Route path="edit/:id" element={<ContentEdit />} />
                  <Route path="show/:id" element={<ContentShow />} />
                </Route>
                <Route path="system-cache" element={<SystemCache />} />
                <Route path="ingredient-catalog">
                  <Route index element={<IngredientCatalogList />} />
                  <Route path="create" element={<IngredientCatalogCreate />} />
                  <Route path="edit/:id" element={<IngredientCatalogEdit />} />
                  <Route path="show/:id" element={<IngredientCatalogShow />} />
                  <Route path="usda-search" element={<UsdaSearch />} />
                </Route>
                <Route path="dish-catalog">
                  <Route index element={<DishCatalogList />} />
                  <Route path="create" element={<DishCatalogCreate />} />
                  <Route path="edit/:id" element={<DishCatalogEdit />} />
                  <Route path="show/:id" element={<DishCatalogShow />} />
                </Route>
                <Route path="tagging">
                  <Route path="scan" element={<TaggingScan />} />
                  <Route path="ai-review" element={<AiReviewList />} />
                  <Route path="quality-cleanup" element={<QualityCleanupList />} />
                  <Route path="logs">
                    <Route index element={<TaggingLogList />} />
                    <Route path="show/:id" element={<TaggingLogShow />} />
                  </Route>
                </Route>
                <Route path="knowledge-bases">
                  <Route index element={<KnowledgeBaseList />} />
                  <Route path="create" element={<KnowledgeBaseCreate />} />
                  <Route path="show/:id" element={<KnowledgeBaseShow />} />
                </Route>
                <Route path="visual-entities">
                  <Route index element={<VisualEntityList />} />
                  <Route path="show/:id" element={<VisualEntityShow />} />
                </Route>
                <Route path="vision-queue-jobs">
                  <Route index element={<VisionQueueJobList />} />
                  <Route path="show/:id" element={<VisionQueueJobShow />} />
                </Route>
                <Route path="surveys">
                  <Route index element={<SurveyList />} />
                  <Route path="create" element={<SurveyCreate />} />
                  <Route path="edit/:id" element={<SurveyEdit />} />
                  <Route path="show/:id" element={<SurveyShow />} />
                </Route>
                <Route path="survey-screens">
                  <Route index element={<SurveyScreenList />} />
                  <Route path="create" element={<SurveyScreenCreate />} />
                  <Route path="edit/:id" element={<SurveyScreenEdit />} />
                  <Route path="show/:id" element={<SurveyScreenShow />} />
                </Route>
                <Route path="survey-questions">
                  <Route index element={<SurveyQuestionList />} />
                  <Route path="create" element={<SurveyQuestionCreate />} />
                  <Route path="edit/:id" element={<SurveyQuestionEdit />} />
                  <Route path="show/:id" element={<SurveyQuestionShow />} />
                </Route>
                <Route path="food-logs">
                  <Route index element={<FoodLogList />} />
                  <Route path="show/:id" element={<FoodLogShow />} />
                </Route>
                <Route path="media-assets">
                  <Route index element={<MediaAssetList />} />
                </Route>
                <Route path="survey-responses">
                  <Route index element={<SurveyResponseList />} />
                  <Route path="show/:id" element={<SurveyResponseShow />} />
                </Route>
                <Route path="equipment-catalog">
                  <Route index element={<EquipmentCatalogList />} />
                  <Route path="create" element={<EquipmentCatalogCreate />} />
                  <Route path="edit/:id" element={<EquipmentCatalogEdit />} />
                  <Route path="show/:id" element={<EquipmentCatalogShow />} />
                </Route>
                <Route path="gym-branches">
                  <Route index element={<GymBranchList />} />
                  <Route path="edit/:id" element={<GymBranchEdit />} />
                </Route>
                <Route path="restaurants">
                  <Route index element={<RestaurantList />} />
                  <Route path="create" element={<RestaurantCreate />} />
                  <Route path="edit/:id" element={<RestaurantEdit />} />
                  <Route path="show/:id" element={<RestaurantShow />} />
                </Route>
                <Route path="meals">
                  <Route index element={<MealList />} />
                  <Route path="create" element={<MealCreate />} />
                  <Route path="edit/:id" element={<MealEdit />} />
                  <Route path="show/:id" element={<MealShow />} />
                </Route>
                <Route path="exercises">
                  <Route index element={<ExerciseList />} />
                  <Route path="create" element={<ExerciseCreate />} />
                  <Route path="edit/:id" element={<ExerciseEdit />} />
                  <Route path="show/:id" element={<ExerciseShow />} />
                </Route>
                <Route path="workout-plans">
                  <Route index element={<WorkoutPlanList />} />
                  <Route path="create" element={<WorkoutPlanCreate />} />
                  <Route path="edit/:id" element={<WorkoutPlanEdit />} />
                  <Route path="show/:id" element={<WorkoutPlanShow />} />
                </Route>
                <Route path="workout-logs">
                  <Route index element={<WorkoutLogList />} />
                  <Route path="show/:id" element={<WorkoutLogShow />} />
                </Route>
                <Route path="sleep-steps">
                  <Route index element={<SleepStepsList />} />
                  <Route path="show/:id" element={<SleepStepsShow />} />
                </Route>
                <Route path="body-metric-logs">
                  <Route index element={<BodyMetricLogList />} />
                  <Route path="show/:id" element={<BodyMetricLogShow />} />
                </Route>
                <Route path="body-metric-sources">
                  <Route index element={<BodyMetricSourceList />} />
                  <Route path="show/:id" element={<BodyMetricSourceShow />} />
                </Route>
                <Route path="health-sync-records">
                  <Route index element={<HealthSyncRecordList />} />
                  <Route path="create" element={<HealthSyncRecordCreate />} />
                  <Route path="edit/:id" element={<HealthSyncRecordEdit />} />
                  <Route path="show/:id" element={<HealthSyncRecordShow />} />
                </Route>
                <Route path="fasting-logs">
                  <Route index element={<FastingLogList />} />
                  <Route path="show/:id" element={<FastingLogShow />} />
                </Route>
                <Route path="mood-logs">
                  <Route index element={<MoodLogList />} />
                  <Route path="show/:id" element={<MoodLogShow />} />
                </Route>
                <Route path="feedback-reports">
                  <Route index element={<FeedbackReportList />} />
                  <Route path="show/:id" element={<FeedbackReportShow />} />
                </Route>
                <Route path="account-deletion-requests">
                  <Route index element={<AccountDeletionRequestList />} />
                  <Route path="show/:id" element={<AccountDeletionRequestShow />} />
                </Route>
                <Route path="subscription-plans">
                  <Route index element={<SubscriptionPlanList />} />
                  <Route path="create" element={<SubscriptionPlanCreate />} />
                  <Route path="edit/:id" element={<SubscriptionPlanEdit />} />
                  <Route path="show/:id" element={<SubscriptionPlanShow />} />
                </Route>
                <Route path="subscriptions">
                  <Route index element={<SubscriptionList />} />
                  <Route path="show/:id" element={<SubscriptionShow />} />
                </Route>
                <Route path="referrals">
                  <Route index element={<ReferralList />} />
                  <Route path="show/:id" element={<ReferralShow />} />
                </Route>
                <Route path="referral-codes">
                  <Route index element={<ReferralCodeList />} />
                  <Route path="create" element={<ReferralCodeCreate />} />
                  <Route path="edit/:id" element={<ReferralCodeEdit />} />
                </Route>
                <Route path="member-permissions">
                  <Route index element={<MemberPermissionsPage />} />
                </Route>
                <Route path="gamification-tasks">
                  <Route index element={<GamificationTaskList />} />
                  <Route path="create" element={<GamificationTaskCreate />} />
                  <Route path="edit/:id" element={<GamificationTaskEdit />} />
                </Route>
                <Route path="pet-skins">
                  <Route index element={<PetSkinList />} />
                  <Route path="create" element={<PetSkinCreate />} />
                  <Route path="edit/:id" element={<PetSkinEdit />} />
                </Route>
                <Route path="pet-skin-categories">
                  <Route index element={<PetSkinCategoryList />} />
                  <Route path="create" element={<PetSkinCategoryCreate />} />
                  <Route path="edit/:id" element={<PetSkinCategoryEdit />} />
                </Route>
                <Route path="badges">
                  <Route index element={<BadgeList />} />
                  <Route path="create" element={<BadgeCreate />} />
                  <Route path="edit/:id" element={<BadgeEdit />} />
                  <Route path="show/:id" element={<BadgeShow />} />
                </Route>
                <Route path="levels">
                  <Route index element={<LevelList />} />
                  <Route path="create" element={<LevelCreate />} />
                  <Route path="edit/:id" element={<LevelEdit />} />
                  <Route path="show/:id" element={<LevelShow />} />
                </Route>
                <Route path="coin-transactions">
                  <Route index element={<CoinTransactionList />} />
                </Route>
                <Route path="xp-transactions">
                  <Route index element={<XpTransactionList />} />
                </Route>
                <Route path="gamification-audit">
                  <Route index element={<GamificationAuditList />} />
                </Route>
                <Route path="gamification-settings">
                  <Route index element={<GamificationSettingsPage />} />
                </Route>
                <Route path="item-grants">
                  <Route index element={<ItemGrantPage />} />
                </Route>
                <Route path="llm-providers">
                  <Route index element={<LlmProviderList />} />
                  <Route path="create" element={<LlmProviderCreate />} />
                  <Route path="edit/:id" element={<LlmProviderEdit />} />
                  <Route path="show/:id" element={<LlmProviderShow />} />
                </Route>
                <Route path="llm-prompts">
                  <Route index element={<LlmPromptList />} />
                  <Route path="create" element={<LlmPromptCreate />} />
                  <Route path="edit/:id" element={<LlmPromptEdit />} />
                  <Route path="show/:id" element={<LlmPromptShow />} />
                </Route>
                <Route path="llm-call-logs">
                  <Route index element={<LlmCallLogList />} />
                  <Route path="show/:id" element={<LlmCallLogShow />} />
                </Route>
                <Route path="llm-models">
                  <Route index element={<LlmModelList />} />
                  <Route path="create" element={<LlmModelCreate />} />
                  <Route path="edit/:id" element={<LlmModelEdit />} />
                  <Route path="show/:id" element={<LlmModelShow />} />
                </Route>
                <Route path="llm-playground">
                  <Route index element={<LlmPlayground />} />
                </Route>
                <Route path="*" element={<ErrorComponent />} />
              </Route>
            </Routes>
            <RefineKbar />
          </Refine>
        </RefineKbarProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
